import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import tw from 'twin.macro'

interface SensitiveDataResponse {
  data: Record<string, string>
  files: string[]
}

interface SensitiveDataWrapperProps {
  children: (value: {
    getValueForField: (fieldKey: string) => string
    sensitiveFiles: string[] | null
  }) => React.ReactNode
  onFetchAllSensitiveData: () => Promise<SensitiveDataResponse>
}

export const SensitiveDataWrapper: React.FC<SensitiveDataWrapperProps> = ({
  children,
  onFetchAllSensitiveData,
}) => {
  const [sensitiveDataCache, setSensitiveDataCache] = useState<Record<string, string> | null>(null)
  const [sensitiveFilesCache, setSensitiveFilesCache] = useState<string[] | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const fetchSensitiveData = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await onFetchAllSensitiveData()
      setSensitiveDataCache(response.data)
      setSensitiveFilesCache(response.files)
    } catch (error) {
      console.error('Error fetching sensitive data:', error)
    } finally {
      setIsLoading(false)
    }
  }, [onFetchAllSensitiveData])

  useEffect(() => {
    fetchSensitiveData()
  }, [fetchSensitiveData])

  const getValueForField = useCallback(
    (fieldKey: string): string => {
      if (!sensitiveDataCache) {
        return ''
      }

      return sensitiveDataCache[fieldKey] || ''
    },
    [sensitiveDataCache],
  )

  if (isLoading) {
    return (
      <div css={tw`relative h-full`}>
        <div css={tw`absolute inset-0 flex items-center justify-center`}>
          <CircularProgress size={40} />
        </div>
      </div>
    )
  }

  return <Fragment>{children({ getValueForField, sensitiveFiles: sensitiveFilesCache })}</Fragment>
}
